import {
  createEntityAdapter,
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import axios from 'axios';

import {
  fetchGetCampaign,
  fetchGetCampaigns,
  fetchGetCampaignAdmins,
  fetchEditCampaign,
} from 'api/campaignApi';
import {
  fetchActionCampaignArtChange,
  fetchAddComment,
  fetchApproveCampaignArt,
  fetchGetCampaignArt,
  fetchRejectCampaignArt,
  fetchRequestCampaignArtChange,
} from 'api/campaignArtApi';
import {
  fetchAddCampaignMessage,
  fetchGetCampaignMessages,
} from 'api/campaignMessagesApi';
import { adminRoles, emptyCampaign } from 'constants/campaignConstants';
import { campaignArtStatuses, designArtStatuses } from 'constants/statuses';
import { parseArtDesignStatus } from 'services/campaignArtServices';
import showSnackbarError from 'utils/showSnackbarError';

const initialState = {
  totalCount: 0,
  totalPages: 0,
  isLoading: false,
  isAllLoading: false,
  isMessagesLoading: false,
  isMessagesUpdating: false,
  isAdminsLoading: false,
  artChangeRequests: [],
};

const campaignsAdapter = createEntityAdapter({
  selectId: (campaign) => campaign.id,
});

export const getCampaigns = createAsyncThunk(
  'campaigns/getCampaigns',
  async (params, { dispatch, rejectWithValue, signal }) => {
    try {
      const source = axios.CancelToken.source();

      signal.addEventListener('abort', () => {
        source.cancel();
      });

      const { artChangeRequests, campaigns, totalCount } =
        await fetchGetCampaigns({
          ...params,
          cancelToken: source.token,
        });

      return { artChangeRequests, campaigns, totalCount };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { isAllLoading } = getState().campaigns;

      if (isAllLoading) {
        return false;
      }

      return true;
    },
  }
);

export const getCampaign = createAsyncThunk(
  'campaigns/getCampaign',
  async ({ id, navigate }, { dispatch, rejectWithValue }) => {
    try {
      const campaign = await fetchGetCampaign({ id });

      if (campaign) {
        const { designArts, artPreferences } = await fetchGetCampaignArt({
          campaignId: id,
        });

        const { workflow, status, billingStatus } = campaign;

        const artStatus = parseArtDesignStatus(
          workflow,
          status,
          designArts,
          artPreferences,
          billingStatus
        );

        return { ...campaign, designArts, artPreferences, artStatus };
      }

      dispatch(showSnackbarError('You have no access to this campaign'));

      navigate('/');

      return campaign;
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { isLoading } = getState().campaigns;

      if (isLoading) {
        return false;
      }

      return true;
    },
  }
);

export const addCampaignPaymentMethod = createAsyncThunk(
  'campaigns/addCampaignPaymentMethod',
  async ({ id, paymentMethodId }, { dispatch, rejectWithValue }) => {
    try {
      const campaign = await fetchEditCampaign({
        id,
        billing_info_id: paymentMethodId,
      });

      return {
        id,
        changes: campaign,
      };
    } catch (e) {
      const error = JSON.parse(e.message);

      if (error?.statusCode === 400 && error?.errors[0]) {
        const { auth_msg: authMsg } = JSON.parse(error.errors[0]);

        if (authMsg === 'A/DECLINED' || authMsg === 'Unknown') {
          dispatch(showSnackbarError('Invalid Card Data'));
        } else {
          dispatch(showSnackbarError(`Error message: ${authMsg}`));
        }
      } else {
        dispatch(showSnackbarError(e.message, e.stack));
      }

      return rejectWithValue(e.message);
    }
  }
);

export const addArtComment = createAsyncThunk(
  'campaigns/addArtComment',
  async (
    { campaignId, postId, comment },
    { getState, dispatch, rejectWithValue }
  ) => {
    const { email } = getState().auth.user;

    try {
      fetchAddComment({ campaignId, artId: postId, note: comment });

      const campaign = getState().campaigns.entities[campaignId];
      const oldDesignArt = campaign.designArts.find((el) => el.id === postId);
      const createdDate = Date.now();
      const newComment = {
        id: `${comment}${createdDate}`,
        message: comment,
        createdDate,
        username: email,
      };

      const updatedArt = {
        ...oldDesignArt,
        comments: [...oldDesignArt.comments, newComment],
      };

      const designArts = campaign.designArts.map((el) =>
        el.id === postId ? updatedArt : el
      );

      return { id: campaignId, changes: { designArts } };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  }
);

export const getCampaignMessages = createAsyncThunk(
  'campaigns/getCampaignMessages',
  async ({ campaignId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { username } = getState().auth.user;

      const messages = await fetchGetCampaignMessages({ username, campaignId });

      return {
        id: campaignId,
        changes: {
          messages: messages.reverse(),
        },
      };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { isMessagesLoading } = getState().campaigns;

      if (isMessagesLoading) {
        return false;
      }

      return true;
    },
  }
);

export const updateCampaignMessages = createAsyncThunk(
  'campaigns/updateCampaignMessages',
  async ({ campaignId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const { username } = getState().auth.user;

      const messages = await fetchGetCampaignMessages({
        username,
        campaignId,
      });

      return {
        id: campaignId,
        changes: {
          messages: messages.reverse(),
        },
      };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.response.data);
    }
  }
);

export const addCampaignMessage = createAsyncThunk(
  'campaigns/addCampaignMessage',
  async (
    { campaignId, message, attachmentUrl },
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      const { username } = getState().auth.user;
      const { messages } = getState().campaigns.entities[campaignId];

      const newMessage = await fetchAddCampaignMessage({
        campaignId,
        message,
        attachmentUrl,
        username,
      });

      return {
        id: campaignId,
        changes: {
          messages: [...messages, newMessage],
        },
      };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  }
);

export const getCampaignAdmins = createAsyncThunk(
  'campaigns/getCampaignAdmins',
  async ({ campaignId }, { dispatch, rejectWithValue }) => {
    try {
      const admins = await fetchGetCampaignAdmins({ campaignId });

      const campaignAdmins = {};

      const artAdmin = admins.filter((el) => el.role === adminRoles.ART)[0];
      const successCoachAdmin = admins.filter(
        (el) => el.role === adminRoles.CS
      )[0];
      const customerCareAdmin = admins.filter(
        (el) => el.role === adminRoles.CUSTOMER_CARE
      )[0];

      if (artAdmin) {
        campaignAdmins[adminRoles.ART] = artAdmin;
      }
      if (successCoachAdmin) {
        campaignAdmins[adminRoles.CS] = successCoachAdmin;
      }
      if (successCoachAdmin) {
        campaignAdmins[adminRoles.CUSTOMER_CARE] = customerCareAdmin;
      }

      return {
        id: campaignId,
        changes: {
          admins: campaignAdmins,
        },
      };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  }
);

export const approveCampaignArt = createAsyncThunk(
  'campaigns/approveCampaignArt',
  async ({ postId, campaignId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const oldCampaign = getState().campaigns.entities[campaignId];

      await fetchApproveCampaignArt({ id: postId, campaignId });

      const campaign = await fetchGetCampaign({
        id: campaignId,
      });

      const updatedArt = {
        ...oldCampaign.designArts.find((el) => el.id === postId),
        status: designArtStatuses.APPROVED,
      };
      const designArts = oldCampaign.designArts.map((el) =>
        el.id === postId ? updatedArt : el
      );
      const { artPreferences } = oldCampaign;
      const artStatus = campaignArtStatuses.ART_ACCEPTED;

      return {
        id: campaignId,
        changes: {
          ...campaign,
          artStatus,
          designArts,
          artPreferences,
        },
      };
    } catch (e) {
      if (e.message.includes('400')) {
        dispatch(
          showSnackbarError(
            'You must first add a payment method to this campaign before you can approve this Art Proof.',
            e.stack
          )
        );
      } else {
        dispatch(showSnackbarError(e.message, e.stack));
      }

      return rejectWithValue(e.message);
    }
  }
);

export const rejectCampaignArt = createAsyncThunk(
  'campaigns/rejectCampaignArt',
  async (
    { postId, campaignId, reason },
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      const oldCampaign = getState().campaigns.entities[campaignId];

      await fetchRejectCampaignArt({ id: postId, campaignId, note: reason });

      const campaign = await fetchGetCampaign({
        id: campaignId,
      });

      const updatedArt = {
        ...oldCampaign.designArts.find((el) => el.id === postId),
        status: designArtStatuses.REJECTED,
      };
      const designArts = oldCampaign.designArts.map((el) =>
        el.id === postId ? updatedArt : el
      );
      const { artPreferences } = oldCampaign;
      const artStatus = campaignArtStatuses.ART_REJECTED;

      return {
        id: campaignId,
        changes: {
          ...campaign,
          artStatus,
          designArts,
          artPreferences,
        },
      };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  }
);

export const requestArtChange = createAsyncThunk(
  'campaigns/requestArtChange',
  async ({ campaignId, goal, files }, { getState, rejectWithValue }) => {
    try {
      const { artChangeRequests } = getState().campaigns.entities[campaignId];

      const result = await fetchRequestCampaignArtChange({
        campaign_id: campaignId,
        description: goal,
        attachments: files,
      });

      return {
        id: campaignId,
        changes: {
          artChangeRequests: [...artChangeRequests, result],
        },
      };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const submitArtChangeQuote = createAsyncThunk(
  'campaigns/submitArtChangeQuote',
  async (
    { comment, artChangeId, campaignId, action },
    { getState, rejectWithValue }
  ) => {
    try {
      const { artChangeRequests } = getState().campaigns.entities[campaignId];

      const result = await fetchActionCampaignArtChange({
        id: artChangeId,
        action,
        reason: comment,
      });

      return {
        id: campaignId,
        changes: {
          artChangeRequests: artChangeRequests.map((el) =>
            el.id === artChangeId ? result : el
          ),
        },
      };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState: campaignsAdapter.getInitialState(initialState),
  reducers: {
    updateCampaign(state, { payload }) {
      campaignsAdapter.setOne(state, payload);
    },
    updateCampaignFields(state, { payload: { id, changes } }) {
      campaignsAdapter.updateOne(state, {
        id,
        changes,
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCampaigns.pending, (state) => {
        state.isAllLoading = true;
      })
      .addCase(getCampaigns.fulfilled, (state, { payload }) => {
        campaignsAdapter.setAll(state, payload.campaigns);
        state.artChangeRequests = payload.artChangeRequests;
        state.totalCount = payload.totalCount;
        state.totalPages = payload.totalPages;
        state.isAllLoading = false;
      })
      .addCase(getCampaigns.rejected, (state) => {
        state.isAllLoading = false;
      })
      .addCase(getCampaign.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCampaign.fulfilled, (state, action) => {
        if (action.payload) {
          campaignsAdapter.upsertOne(state, action.payload);
        }

        state.isLoading = false;
      })
      .addCase(getCampaign.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(addCampaignPaymentMethod.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addCampaignPaymentMethod.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isLoading = false;
      })
      .addCase(addCampaignPaymentMethod.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getCampaignMessages.pending, (state) => {
        state.isMessagesLoading = true;
      })
      .addCase(getCampaignMessages.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isMessagesLoading = false;
      })
      .addCase(getCampaignMessages.rejected, (state) => {
        state.isMessagesLoading = false;
      })
      .addCase(updateCampaignMessages.pending, (state) => {
        state.isMessagesUpdating = true;
      })
      .addCase(updateCampaignMessages.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isMessagesUpdating = false;
      })
      .addCase(updateCampaignMessages.rejected, (state) => {
        state.isMessagesUpdating = false;
      })
      .addCase(addCampaignMessage.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
      })
      .addCase(addArtComment.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
      })
      .addCase(getCampaignAdmins.pending, (state) => {
        state.isAdminsLoading = true;
      })
      .addCase(getCampaignAdmins.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isAdminsLoading = false;
      })
      .addCase(getCampaignAdmins.rejected, (state) => {
        state.isAdminsLoading = false;
        state.isAdminsLoaded = true;
      })
      .addCase(rejectCampaignArt.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
      })
      .addCase(approveCampaignArt.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
      })
      .addCase(requestArtChange.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
      })
      .addCase(submitArtChangeQuote.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
      });
  },
});

const { selectAll, selectById } = campaignsAdapter.getSelectors(
  (state) => state.campaigns
);

const all = createSelector(selectAll, (state) => state);

const byId = (id) =>
  createSelector(
    (state) => state,
    (state) => selectById(state, id) || emptyCampaign
  );

const selectSelf = (state) => state.campaigns;
const isAllLoading = createSelector(selectSelf, (state) => state.isAllLoading);
const isLoading = createSelector(selectSelf, (state) => state.isLoading);
const isMessagesLoading = createSelector(
  selectSelf,
  (state) => state.isMessagesLoading
);
const isMessagesUpdating = createSelector(
  selectSelf,
  (state) => state.isMessagesUpdating
);
const isAdminsLoading = createSelector(
  selectSelf,
  (state) => state.isAdminsLoading
);
const totalCount = createSelector(selectSelf, (state) => state.totalCount);
const totalPages = createSelector(selectSelf, (state) => state.totalPages);
const artChangeRequests = createSelector(
  selectSelf,
  (state) => state.artChangeRequests
);

export const CampaignsSelectors = {
  all,
  byId,
  isAllLoading,
  isLoading,
  isMessagesLoading,
  isMessagesUpdating,
  isAdminsLoading,
  totalCount,
  totalPages,
  artChangeRequests,
};

export const { updateCampaign, updateCampaignFields } = campaignsSlice.actions;

export default campaignsSlice.reducer;
